import { AsperaError } from '../AsperaError';
import { defaultPageSizes, storageKeys } from '../../constants/constants';
import { getJsonStorageItem, setJsonStorageItem } from '../../helpers/starterHelpers';
import { iterableToArray } from '../../helpers/helpers';
import { tracking } from '../../services/TrackingService';
/**
 * DataTable params for paging and filtering
 */
export class AsperaTableData {
    constructor(tableId, forceGeneric, groupTableId, doNotSaveAdvancedFilters) {
        /** The available page sizes for the table */
        this.pageSizes = defaultPageSizes;
        /** Map of other table data */
        this.tableDataMap = new Map();
        /** Map of other custom table data */
        this.tableCustomDataMap = new Map();
        /** Key to use for tracking generic tables */
        this.genericKey = 'GENERIC';
        /** Indicate if advanced filters should not be saved */
        this.doNotSaveAdvancedFilters = false;
        const id = forceGeneric ? this.genericKey : (tableId || this.genericKey);
        this.doNotSaveAdvancedFilters = doNotSaveAdvancedFilters || false;
        let initialData;
        if (id !== this.genericKey) {
            initialData = this.getInitialData(tableId);
        }
        const initialCustomData = this.getInitialCustomData(groupTableId || tableId);
        if (initialCustomData) {
            this.tableCustomData = initialCustomData;
        }
        else {
            this.tableCustomData = {
                id: groupTableId || tableId || this.genericKey,
            };
        }
        if (initialData) {
            this.tableData = initialData;
        }
        else {
            this.tableData = {
                id,
                currentPageSize: this.validatePerPageSize(tracking.getUserPreference('resultsPerPage') || this.pageSizes[1].value),
                page: 1,
                filterText: undefined,
                sort: undefined,
            };
        }
    }
    getInitialData(tableId) {
        if (!tableId)
            return undefined;
        const currentStorage = getJsonStorageItem(storageKeys.tableData) || [];
        currentStorage.forEach(item => this.tableDataMap.set(item.id, item));
        const dataFromMap = this.tableDataMap.get(tableId);
        if (dataFromMap) {
            return dataFromMap;
        }
        return undefined;
    }
    getInitialCustomData(tableId) {
        if (!tableId)
            return undefined;
        const currentStorage = getJsonStorageItem(storageKeys.tableDataCustomization) || [];
        currentStorage.forEach(item => this.tableCustomDataMap.set(item.id, item));
        const dataFromMap = this.tableCustomDataMap.get(tableId);
        if (dataFromMap) {
            return dataFromMap;
        }
        return undefined;
    }
    /**
     * Set the current table data into storage.  This is called every time a set happens on table data
     */
    setTableInMemory() {
        if (this.tableCustomData.id === this.genericKey)
            return;
        this.tableCustomDataMap.set(this.tableCustomData.id, this.tableCustomData);
        const arrayCustomItems = iterableToArray(this.tableCustomDataMap.values());
        setJsonStorageItem(storageKeys.tableDataCustomization, arrayCustomItems);
        if (this.tableData.id === this.genericKey)
            return;
        this.tableDataMap.set(this.tableData.id, this.tableData);
        const arrayItems = iterableToArray(this.tableDataMap.values());
        if (arrayItems.length > 20) {
            if (this.tableData.id === arrayItems[0].id) {
                arrayItems.pop();
            }
            else {
                arrayItems.shift();
            }
        }
        setJsonStorageItem(storageKeys.tableData, arrayItems);
    }
    /**
     * Validate that the requested size can be used (available in dropdown)
     *
     * @param newSize - size to use for results per page.
     *
     * @returns the size requested or the default size.  Logs to sentry if attempt to use bad size happens
     */
    validatePerPageSize(newSize) {
        newSize = Number(newSize);
        const foundValidPageSize = this.pageSizes.filter(item => item.value === newSize)[0];
        if (!foundValidPageSize) {
            new AsperaError('AsperaTableData: Attempted to set page size to invalid size', { size: newSize, availableSizes: this.pageSizes });
            return this.pageSizes[1].value;
        }
        return newSize;
    }
    /** Indicate if sort is reversed */
    get isSortReversed() {
        var _a;
        return !!(((_a = this.tableData.sort) === null || _a === void 0 ? void 0 : _a.indexOf('-')) === 0);
    }
    /** Get sort key for API (this includes the reverse token) */
    get apiSortKey() {
        return this.tableData.sort;
    }
    /** Get the sort key for App (this does not include the reverse key) */
    get sortKey() {
        if (this.tableData.sort) {
            return this.isSortReversed ? this.tableData.sort.slice(1) : this.tableData.sort;
        }
        return undefined;
    }
    /** Set the sort key, this should include the reverse key */
    set sortKey(key) {
        this.tableData.sort = key;
        this.setTableInMemory();
    }
    /** Set current page size. NOTE if invalid page size it will default to first option and log to sentry */
    set pageSize(newSize) {
        this.tableData.currentPageSize = this.validatePerPageSize(newSize);
        this.setTableInMemory();
    }
    /** Get the current page size */
    get pageSize() {
        return this.tableData.currentPageSize || this.pageSizes[1].value;
    }
    /** Set the current page */
    set page(newPage) {
        this.tableData.page = newPage;
        this.setTableInMemory();
    }
    /** Get the current page */
    get page() {
        return this.tableData.page;
    }
    /** Set the filter text */
    set filterText(text) {
        this.tableData.filterText = text;
        this.setTableInMemory();
    }
    /** Get the filter text */
    get filterText() {
        return this.tableData.filterText;
    }
    /** Get the base64 data for query param */
    get queryParamData() {
        return window.btoa(JSON.stringify(this.tableData));
    }
    /** Indicate if the advanced filter data is set (this is quicker check verse the primary getter) */
    get hasAdvancedFilterModel() {
        return !!this.tableData.advancedFilterModel;
    }
    /** Advanced filters in memory */
    set advancedFilterModel(item) {
        if (item && typeof item === 'object' && !this.doNotSaveAdvancedFilters) {
            try {
                this.tableData.advancedFilterModel = JSON.stringify(item);
                this.setTableInMemory();
            }
            catch (error) {
                new AsperaError('AsperaTableData: unable to save advanced filters', error);
            }
        }
    }
    /** Advanced filters in memory */
    get advancedFilterModel() {
        if (this.tableData.advancedFilterModel && typeof this.tableData.advancedFilterModel === 'string') {
            try {
                return JSON.parse(this.tableData.advancedFilterModel);
            }
            catch (error) {
                new AsperaError('AsperaTableData: unable to parse advanced filters', error);
                return {};
            }
        }
        return {};
    }
    /** Table sizing in memory */
    setTableSizing(key, size) {
        const newTableSize = this.tableSizing;
        if (size) {
            newTableSize[key] = size;
        }
        else {
            delete newTableSize[key];
        }
        try {
            this.tableCustomData.tableSizing = JSON.stringify(newTableSize);
            this.setTableInMemory();
        }
        catch (error) {
            new AsperaError('AsperaTableData: unable to save table sizing', error);
        }
    }
    /** Table sizing in memory */
    getTableSizing(key) {
        const newTableSize = this.tableSizing;
        return newTableSize[key] || undefined;
    }
    /** Table sizing in memory */
    get tableSizing() {
        if (this.tableCustomData.tableSizing && typeof this.tableCustomData.tableSizing === 'string') {
            try {
                return JSON.parse(this.tableCustomData.tableSizing);
            }
            catch (error) {
                new AsperaError('AsperaTableData: unable to parse table sizing', error);
                return {};
            }
        }
        return {};
    }
    /** Clears table sizing in memory */
    clearTableSizing() {
        this.tableCustomData.tableSizing = undefined;
        this.setTableInMemory();
    }
    /** Clears advanced filters in memory */
    clearAdvancedFilters() {
        this.tableData.advancedFilterModel = undefined;
        this.setTableInMemory();
    }
}
